import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// AlarmTicketPage.jsx - FIXED: Eliminando bucle infinito y errores
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Card,
    CardContent,
    Grid,
    Tooltip,
    Alert,
    CircularProgress,
    Collapse,
    List,
    ListItem,
    ListItemText,
    InputAdornment,
    Switch,
    FormControlLabel,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Visibility as ViewIcon,
    CheckCircle as ResolveIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    History as HistoryIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Download as ExportIcon,
    Assignment as TicketIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

import { useNotification } from '../components/common/GlobalNotifications';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api'; // 🔧 FIXED: Usar servicio API
import AIRecommendationsPanel from '../components/ai/AIRecommendationsPanel';
import AskAIButton from '../components/ai/AskAIButton';
import { isDualYoyoTicket, buildDualContext } from '../utils/dualYoyo';

// Status colors and labels
const TICKET_STATUS_COLORS = {
    open: 'error',
    acknowledged: 'warning',
    resolved: 'success'
};

const TICKET_STATUS_LABELS = {
    open: i18n.t('alarmTickets.status.open'),
    acknowledged: i18n.t('alarmTickets.status.acknowledged'),
    resolved: i18n.t('alarmTickets.status.resolved')
};

const BREACH_TYPE_LABELS = {
    MIN_BREACH: i18n.t('hardcoded.components_alarmprofiles_profileform.minimo'),
    MAX_BREACH: i18n.t('hardcoded.pages_billingplanspage.maximo')
};

const normalizeMetricLabel = (value, ticket = null) => {
    // Si tenemos el ticket entero, usar el alarm_reason para distinguir Dual y no-report.
    if (ticket) {
        const reason = ticket.alarm_reason;
        if (reason === 'dual_yoyo') return i18n.t('hardcoded.pages_alarmticketpage.dual');
        if (reason === 'sensor_no_report') return i18n.t('hardcoded.pages_alarmticketpage.sin_reporte_sensor');
        if (reason === 'receiver_no_report') return i18n.t('hardcoded.pages_alarmticketpage.sin_reporte_receptor');
    }
    if (!value) return '';
    const v = `${value}`.toUpperCase();
    if (v === 'NO_REPORT') return i18n.t('hardcoded.pages_alarmticketpage.sin_reporte');
    return `${value}`.replace(/psi/gi, 'PSI');
};

const normalizeUnitLabel = (value) => {
    if (!value) return '';
    const v = `${value}`.toLowerCase();
    if (v === 'elapsed_seconds' || v === 'h' || v === 'horas') return '';
    return `${value}`.replace(/psi/gi, 'PSI');
};

const formatHumanElapsed = (hoursOrSeconds, unit) => {
    if (hoursOrSeconds == null) return '—';
    let totalSeconds;
    const u = `${unit || ''}`.toLowerCase();
    if (u === 'elapsed_seconds' || u === 'seconds' || u === 's') {
        totalSeconds = Number(hoursOrSeconds);
    } else {
        // assume hours by default for NO_REPORT tickets
        totalSeconds = Number(hoursOrSeconds) * 3600;
    }
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
    const days = Math.floor(totalSeconds / 86400);
    const rem = totalSeconds % 86400;
    const hours = Math.floor(rem / 3600);
    const minutes = Math.floor((rem % 3600) / 60);
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
};

const isNoReport = (ticket) => `${ticket?.metric_type || ''}`.toUpperCase() === 'NO_REPORT';

const formatTicketValue = (ticket) => {
    if (!ticket) return '—';
    if (isNoReport(ticket)) {
        return formatHumanElapsed(ticket.current_value, ticket.unit);
    }
    // Dual: el "valor" es la diferencia entre ambas posiciones, no una sola lectura.
    if (isDualYoyoTicket(ticket)) {
        const ctx = buildDualContext(ticket);
        if (ctx) {
            if (ctx.suspended) return i18n.t('hardcoded.pages_alarmticketpage.suspendido');
            if (ctx.valueActualDisplay && ctx.valueActualDisplay !== '—') return ctx.valueActualDisplay;
        }
    }
    const v = Number(ticket.current_value);
    if (!Number.isFinite(v)) return '—';
    return `${v.toFixed(1)} ${normalizeUnitLabel(ticket.unit)}`;
};

const AlarmTicketPage = () => {
  const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotification();
    const { currentCompany } = useAuth();

    // State
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false); // No cargar automáticamente
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null); // Estado de error

    // Filters
    // Solo se permiten 3 estados: open (Abiertos), acknowledged (Vistos), resolved (Resueltos).
    // La opción "Todos" fue removida del UI; el default es "Abiertos".
    const [statusFilters, setStatusFilters] = useState(['open']);
    const [vehicleFilter, setVehicleFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [metricFilter, setMetricFilter] = useState('');
    const [hasSubticketsFilter, setHasSubticketsFilter] = useState('');
    const [sortBy, setSortBy] = useState('recent_unattended_first');
    const [sortOrder, setSortOrder] = useState(t('hardcoded.components_common_advanceddatatable.desc'));

    // Dialogs
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [resolutionNotes, setResolutionNotes] = useState('');

    // UI state
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [showSubtickets, setShowSubtickets] = useState(false);

    // 🔧 FIXED: Usar useRef para evitar dependencias inestables
    const filtersRef = useRef({ statusFilters, vehicleFilter, priorityFilter, metricFilter, hasSubticketsFilter, sortBy, sortOrder, page, rowsPerPage, searchQuery, showSubtickets });
    filtersRef.current = { statusFilters, vehicleFilter, priorityFilter, metricFilter, hasSubticketsFilter, sortBy, sortOrder, page, rowsPerPage, searchQuery, showSubtickets };

    // 🔧 FIXED: Fetch tickets con dependencias estables
    const fetchTickets = useCallback(async (forceRefresh = false) => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                limit: filtersRef.current.rowsPerPage,
                offset: filtersRef.current.page * filtersRef.current.rowsPerPage,
                include_stats: true
            };

            if (filtersRef.current.statusFilters?.length) {
                params.status_filters = filtersRef.current.statusFilters.join(',');
            }
            if (filtersRef.current.vehicleFilter) {
                params.vehicle_id = filtersRef.current.vehicleFilter;
            }
            if (filtersRef.current.priorityFilter) {
                params.priority = filtersRef.current.priorityFilter;
            }
            if (filtersRef.current.metricFilter) {
                params.metric_type = filtersRef.current.metricFilter;
            }
            if (filtersRef.current.hasSubticketsFilter) {
                params.has_subtickets = filtersRef.current.hasSubticketsFilter === 'with' ? true : false;
            }

            // Por defecto solo mostrar tickets padre; si showSubtickets=true mostrar todos
            if (!filtersRef.current.showSubtickets) {
                params.is_subticket = false;
            }

            // Búsqueda por folio/número de ticket
            if (filtersRef.current.searchQuery) {
                params.search = filtersRef.current.searchQuery;
            }

            params.sort_by = filtersRef.current.sortBy;
            params.sort_order = filtersRef.current.sortOrder;

            // 🔧 FIXED: Usar servicio API en lugar de fetch directo
            const response = await api.alarmTickets.getWithStats(params);

            if (response.data) {
                setTickets(response.data.tickets || []);
                setTotalCount(response.data.total_count || 0);
                setStats(response.data.stats || null);

                console.log(t('hardcoded.pages_alarmticketpage.tickets_loaded_successfully'), response.data.tickets?.length || 0);
            }

        } catch (error) {
            console.error(t('hardcoded.pages_alarmticketpage.error_fetching_tickets'), error);

            // 🔧 FIXED: Manejo específico de error 404
            if (error.response?.status === 404) {
                setError(t('hardcoded.pages_alarmticketpage.el_sistema_de_tickets_de'));
            } else {
                setError(t('hardcoded.pages_alarmticketpage.error_cargando_tickets_intentelo_de'));
                showError(t('hardcoded.pages_alarmticketpage.error_cargando_tickets'));
            }

            // Resetear estados en caso de error
            setTickets([]);
            setTotalCount(0);
            setStats(null);
        } finally {
            setLoading(false);
        }
    }, []); // 🔧 FIXED: Sin dependencias que causen re-creación

    // 🔧 FIXED: Effect que se ejecuta solo cuando es necesario
    useEffect(() => {
        let isMounted = true;

        const loadInitialData = async () => {
            if (isMounted) {
                await fetchTickets();
            }
        };

        loadInitialData();

        return () => {
            isMounted = false;
        };
    }, []); // 🔧 FIXED: Solo se ejecuta al montar

    // 🔧 FIXED: Effect separado para cambios de filtros/paginación
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchTickets();
        }, 300); // Debounce de 300ms

        return () => clearTimeout(timeoutId);
    }, [page, rowsPerPage, statusFilters, vehicleFilter, priorityFilter, metricFilter, hasSubticketsFilter, sortBy, sortOrder, fetchTickets, searchQuery, showSubtickets]);

    // Handle acknowledge tickets
    const handleAcknowledgeTickets = async (ticketIds) => {
        try {
            // 🔧 FIXED: Usar servicio API
            await api.alarmTickets.acknowledge(ticketIds);

            showSuccess(t('hardcoded.pages_alarmticketpage.length_tickets_marcados_como_vistos', { length: ticketIds.length }));
            fetchTickets(true); // Refresh data

        } catch (error) {
            console.error(t('hardcoded.components_schematics_alarmmanager.error_acknowledging_tickets'), error);
            showError(t('hardcoded.pages_alarmticketpage.error_marcando_tickets_como_vistos'));
        }
    };

    // Handle resolve ticket manually
    const handleResolveTicket = async () => {
        if (!selectedTicket || !resolutionNotes.trim()) {
            showError(t('hardcoded.pages_alarmticketdetailpage.debe_proporcionar_notas_de_resolucion'));
            return;
        }

        try {
            // 🔧 FIXED: Usar servicio API
            await api.alarmTickets.resolve(selectedTicket.id, resolutionNotes);

            showSuccess(t('hardcoded.hooks_usetickets.ticket_resuelto_exitosamente'));
            setResolveDialogOpen(false);
            setResolutionNotes('');
            setSelectedTicket(null);
            fetchTickets(true); // Refresh data

        } catch (error) {
            console.error(t('hardcoded.pages_alarmticketdetailpage.error_resolving_ticket'), error);
            showError(t('hardcoded.pages_alarmticketdetailpage.error_resolviendo_ticket'));
        }
    };

    // Toggle expanded row
    const toggleExpandedRow = (ticketId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(ticketId)) {
            newExpanded.delete(ticketId);
        } else {
            newExpanded.add(ticketId);
        }
        setExpandedRows(newExpanded);
    };

    // Format duration
    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return `${hours}h ${remainingMinutes}m`;
    };

    // Create value history chart data
    const createChartData = (valueHistory) => {
        if (!valueHistory || valueHistory.length === 0) return [];

        return valueHistory.map((entry, index) => ({
            time: new Date(entry.timestamp).toLocaleTimeString(),
            value: entry.value,
            status: entry.status
        }));
    };

    // Reset filters
    const resetFilters = () => {
        setStatusFilters(['open', 'acknowledged']);
        setVehicleFilter('');
        setPriorityFilter('');
        setMetricFilter('');
        setHasSubticketsFilter('');
        setSortBy('recent_unattended_first');
        setSortOrder(t('hardcoded.components_common_advanceddatatable.desc'));
        setSearchQuery('');
        setShowSubtickets(false);
        setPage(0);
    };

    // 🔧 FIXED: Función de refresh manual
    const handleRefresh = () => {
        fetchTickets(true);
    };

    return (
        <Box>
            {/* Error inline - sin bloquear la pantalla */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button size="small" onClick={handleRefresh} disabled={loading}>
                            {loading ? t('hardcoded.pages_alarmticketpage.reintentando') : t('hardcoded.pages_alarmticketpage.reintentar')}
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight={600} color="primary.main">
                        {t('hardcoded.pages_alarmticketpage.tickets_de_alarma')}</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        {loading ? t('profile.security.updating') : t('audit.actions.UPDATE')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ExportIcon />}
                        disabled={loading}
                    >
                        {i18n.t('audit.export.title')}</Button>
                    <AskAIButton prompt={t('hardcoded.pages_alarmticketpage.resume_los_tickets_criticos_abiertos')} label={t('hardcoded.components_ai_airecommendationspanel.analizar_con_ia')} />
                </Box>
            </Box>

            <AIRecommendationsPanel module="tickets" />

            {/* Stats Cards — solo 3 estados: Abiertos / Vistos / Resueltos.
                Se removió la tarjeta "Total Tickets" por requerimiento (no hay opción "Todos"). */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setStatusFilters(['open'])}>
                                <Typography variant="h4" color="error.main" fontWeight={600}>
                                    {stats.open_tickets}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {i18n.t('vehicleTicketsDrawer.openLabel')}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setStatusFilters(['acknowledged'])}>
                                <Typography variant="h4" color="warning.main" fontWeight={600}>
                                    {stats.acknowledged_tickets}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {i18n.t('vehicleTicketsDrawer.acknowledgedLabel')}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setStatusFilters(['resolved'])}>
                                <Typography variant="h4" color="success.main" fontWeight={600}>
                                    {stats.resolved_tickets}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {i18n.t('hardcoded.pages_alarmticketpage.resueltos')}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                {/* Buscador */}
                <Box sx={{ mb: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder={t('hardcoded.pages_alarmticketpage.buscar_por_folio_o_numero')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ width: { xs: '100%', md: 380 } }}
                    />
                </Box>

                {/* Filtros */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>{i18n.t('gps.table.status')}</InputLabel>
                        <Select
                            multiple
                            value={statusFilters}
                            onChange={(e) => {
                                // Si el usuario intenta dejar vacío, forzamos "open" para
                                // evitar que se interprete como "Todos" (opción eliminada).
                                const next = e.target.value;
                                setStatusFilters(next && next.length ? next : ['open']);
                            }}
                            label={t('gps.table.status')}
                        >
                            <MenuItem value="open">{i18n.t('vehicleTicketsDrawer.openLabel')}</MenuItem>
                            <MenuItem value="acknowledged">{i18n.t('vehicleTicketsDrawer.acknowledgedLabel')}</MenuItem>
                            <MenuItem value="resolved">{i18n.t('hardcoded.pages_alarmticketpage.resueltos')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>{t('hardcoded.components_tickets_ticketactioncenter.prioridad')}</InputLabel>
                        <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} label={t('hardcoded.components_tickets_ticketactioncenter.prioridad')}>
                            <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                            <MenuItem value="HIGH">{i18n.t('hardcoded.pages_alarmticketpage.alta')}</MenuItem>
                            <MenuItem value="MEDIUM">{t('pages_alarmticketpage.priority_medium')}</MenuItem>
                            <MenuItem value="LOW">{t('pages_alarmticketpage.priority_low')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>{t('hardcoded.components_alarms_alarmlist.metrica')}</InputLabel>
                        <Select value={metricFilter} onChange={(e) => setMetricFilter(e.target.value)} label={t('hardcoded.components_alarms_alarmlist.metrica')}>
                            <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                            <MenuItem value="PSI">PSI</MenuItem>
                            <MenuItem value="TEMP">TEMP</MenuItem>
                            <MenuItem value="ACC">ACC</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel>{i18n.t('hardcoded.pages_adminstorepage.orden')}</InputLabel>
                        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label={t('hardcoded.pages_adminstorepage.orden')}>
                            <MenuItem value="recent_unattended_first">{t('hardcoded.pages_alarmticketpage.sin_atender_recientes_primero')}</MenuItem>
                            <MenuItem value="created_at">{t('hardcoded.pages_alarmticketpage.fecha_creacion')}</MenuItem>
                            <MenuItem value="priority">{t('hardcoded.components_tickets_ticketactioncenter.prioridad')}</MenuItem>
                            <MenuItem value="duration">{t('hardcoded.pages_alarmticketdetailpage.duracion')}</MenuItem>
                            <MenuItem value="status">{i18n.t('gps.table.status')}</MenuItem>
                            <MenuItem value="vehicle">{t('vehicles.table.vehicle')}</MenuItem>
                            <MenuItem value="value">{i18n.t('hardcoded.components_admin_productform.valor')}</MenuItem>
                            <MenuItem value="metric">{t('hardcoded.components_alarms_alarmlist.metrica')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>{i18n.t('hardcoded.pages_alarmticketpage.sentido')}</InputLabel>
                        <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} label={t('hardcoded.pages_alarmticketpage.sentido')}>
                            <MenuItem value="desc">{i18n.t('hardcoded.components_common_advanceddatatable.desc')}</MenuItem>
                            <MenuItem value="asc">{i18n.t('hardcoded.components_common_advanceddatatable.asc')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showSubtickets}
                                onChange={(e) => setShowSubtickets(e.target.checked)}
                            />
                        }
                        label={<Typography variant="body2">{i18n.t('hardcoded.pages_alarmticketpage.mostrar_subtickets')}</Typography>}
                        sx={{ ml: 0.5 }}
                    />

                    {showSubtickets && (
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>{i18n.t('hardcoded.pages_alarmticketpage.filtrar_subtickets')}</InputLabel>
                            <Select value={hasSubticketsFilter} onChange={(e) => setHasSubticketsFilter(e.target.value)} label={t('hardcoded.pages_alarmticketpage.filtrar_subtickets')}>
                                <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                <MenuItem value="with">{t('hardcoded.pages_alarmticketpage.con_subtickets')}</MenuItem>
                                <MenuItem value="without">{t('hardcoded.pages_alarmticketpage.sin_subtickets')}</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <Button
                        variant="outlined"
                        startIcon={<FilterIcon />}
                        onClick={resetFilters}
                        size="small"
                    >
                        {i18n.t('common.clear')}</Button>
                </Box>
            </Paper>

            {/* Main Content */}
            {loading && tickets.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : tickets.length === 0 && !error ? (
                <Paper data-tutorial-id="tickets-area" sx={{ p: 4, textAlign: 'center' }}>
                    <TicketIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {searchQuery ? t('hardcoded.pages_alarmticketpage.sin_resultados_para_searchquery', { searchQuery: searchQuery }) : t('hardcoded.pages_alarmticketpage.no_hay_tickets_de_alarma')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchQuery
                            ? t('hardcoded.pages_alarmticketpage.intenta_con_otro_folio_o')
                            : t('hardcoded.pages_alarmticketpage.los_tickets_se_crean_automaticamente')}
                    </Typography>
                </Paper>
            ) : (
                <TableContainer
                    data-tutorial-id="tickets-area"
                    component={Paper}
                    sx={{ width: '100%', overflowX: 'auto' }}
                >
                    <Table sx={{ minWidth: { xs: 720, sm: 'auto' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>{i18n.t('hardcoded.components_ai_aientitycards.ticket')}</TableCell>
                                <TableCell>{t('vehicles.table.vehicle')}</TableCell>
                                <TableCell>{i18n.t('alarms.list.position')}</TableCell>
                                <TableCell>{t('hardcoded.components_alarms_alarmlist.metrica')}</TableCell>
                                <TableCell>{i18n.t('hardcoded.components_admin_productform.valor')}</TableCell>
                                <TableCell>{i18n.t('gps.table.status')}</TableCell>
                                <TableCell>{t('hardcoded.pages_alarmticketdetailpage.duracion')}</TableCell>
                                <TableCell>{t('common.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tickets.map((ticket) => (
                                <React.Fragment key={ticket.id}>
                                    {/* Main Row */}
                                    <TableRow
                                        hover
                                        onClick={() => navigate(`/alarm-tickets/${ticket.id}`)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpandedRow(ticket.id)}
                                            >
                                                {expandedRows.has(ticket.id) ? <CollapseIcon /> : <ExpandIcon />}
                                            </IconButton>
                                        </TableCell>

                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {ticket.ticket_number || `#${ticket.id.slice(-8)}`}
                                                </Typography>
                                                {ticket.is_subticket && (
                                                    <Chip
                                                        label={t('hardcoded.pages_alarmticketpage.subticket')}
                                                        size="small"
                                                        color="info"
                                                        sx={{ height: 18, fontSize: '0.65rem', mt: 0.25 }}
                                                    />
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(ticket.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2">{ticket.vehicle_name}</Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2">{i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.pos')}{ticket.position}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {i18n.t('hardcoded.pages_alarmticketpage.eje')}{ticket.axle})
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={normalizeMetricLabel(ticket.metric_type, ticket)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    color={ticket.status === 'open' ? 'error.main' : 'text.primary'}
                                                >
                                                    {formatTicketValue(ticket)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {BREACH_TYPE_LABELS[ticket.breach_type]} {i18n.t('hardcoded.pages_alarmticketpage.violado')}</Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={TICKET_STATUS_LABELS[ticket.status]}
                                                color={TICKET_STATUS_COLORS[ticket.status]}
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDuration(ticket.duration_minutes)}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {ticket.status === 'open' && (
                                                    <Tooltip title={t('hardcoded.components_schematics_alarmmanager.marcar_como_visto')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleAcknowledgeTickets([ticket.id])}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {ticket.status !== 'resolved' && (
                                                    <Tooltip title={t('hardcoded.pages_alarmticketdetailpage.resolver_manualmente')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedTicket(ticket);
                                                                setResolveDialogOpen(true);
                                                            }}
                                                        >
                                                            <ResolveIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                <Tooltip title={t('hardcoded.components_admin_ordermanagement.ver_detalles')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedTicket(ticket);
                                                            setDetailsOpen(true);
                                                        }}
                                                    >
                                                        <HistoryIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {/* Expanded Row */}
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ py: 0 }}>
                                            <Collapse in={expandedRows.has(ticket.id)} timeout="auto">
                                                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                    <Grid container spacing={2}>
                                                        {/* Value History Chart */}
                                                        <Grid item xs={12} md={8}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                {t('hardcoded.pages_alarmticketdetailpage.historial_de_valores')}</Typography>
                                                            {ticket.value_history && ticket.value_history.length > 1 ? (
                                                                <ResponsiveContainer width="100%" height={200}>
                                                                    <LineChart data={createChartData(ticket.value_history)}>
                                                                        <CartesianGrid strokeDasharray="3 3" />
                                                                        <XAxis dataKey="time" />
                                                                        <YAxis />
                                                                        <RechartsTooltip />
                                                                        <Line
                                                                            type="monotone"
                                                                            dataKey="value"
                                                                            stroke={theme.palette.primary.main}
                                                                            strokeWidth={2}
                                                                        />
                                                                    </LineChart>
                                                                </ResponsiveContainer>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {t('hardcoded.pages_alarmticketpage.no_hay_suficientes_datos_para')}</Typography>
                                                            )}
                                                        </Grid>

                                                        {/* Ticket Info */}
                                                        <Grid item xs={12} md={4}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                {t('hardcoded.pages_alarmticketpage.informacion_del_ticket')}</Typography>
                                                            <List dense>
                                                                {isNoReport(ticket) ? (
                                                                    <ListItem>
                                                                        <ListItemText
                                                                            primary={t('hardcoded.components_schematics_vehicleticketsdrawer.tiempo_sin_reportar')}
                                                                            secondary={formatHumanElapsed(ticket.current_value, ticket.unit)}
                                                                        />
                                                                    </ListItem>
                                                                ) : (
                                                                    <>
                                                                        <ListItem>
                                                                            <ListItemText
                                                                                primary={t('hardcoded.pages_alarmticketpage.valor_original')}
                                                                                secondary={`${ticket.original_value} ${normalizeUnitLabel(ticket.unit)}`}
                                                                            />
                                                                        </ListItem>
                                                                        <ListItem>
                                                                            <ListItemText
                                                                                primary={t('hardcoded.pages_alarmticketpage.umbral_violado')}
                                                                                secondary={
                                                                                    ticket.breach_type === 'MIN_BREACH'
                                                                                        ? t('hardcoded.pages_alarmticketpage.minimo_threshold_min_normalizeunitla', { threshold_min: ticket.threshold_min, normalizeUnitLa: normalizeUnitLabel(ticket.unit) })
                                                                                        : t('hardcoded.pages_alarmticketpage.maximo_threshold_max_normalizeunitla', { threshold_max: ticket.threshold_max, normalizeUnitLa: normalizeUnitLabel(ticket.unit) })
                                                                                }
                                                                            />
                                                                        </ListItem>
                                                                    </>
                                                                )}
                                                                {ticket.acknowledged_by && (
                                                                    <ListItem>
                                                                        <ListItemText
                                                                            primary={t('hardcoded.pages_alarmticketpage.visto_por')}
                                                                            secondary={ticket.acknowledged_by}
                                                                        />
                                                                    </ListItem>
                                                                )}
                                                                {ticket.resolution_notes && (
                                                                    <ListItem>
                                                                        <ListItemText
                                                                            primary={t('hardcoded.pages_alarmticketdetailpage.notas_de_resolucion')}
                                                                            secondary={ticket.resolution_notes}
                                                                        />
                                                                    </ListItem>
                                                                )}
                                                                {ticket.parent_ticket_id && (
                                                                    <ListItem>
                                                                        <ListItemText
                                                                            primary={t('hardcoded.pages_alarmticketpage.ticket_padre')}
                                                                            secondary={ticket.parent_ticket_id}
                                                                        />
                                                                    </ListItem>
                                                                )}
                                                            </List>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}

            {/* Ticket Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    {t('hardcoded.pages_alarmticketpage.detalles_del_ticket')}{selectedTicket?.id?.slice(-8)}
                </DialogTitle>
                <DialogContent>
                    {selectedTicket && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        {t('hardcoded.components_schematics_vehicleticketsdrawer.informacion_general')}</Typography>
                                    <List>
                                        <ListItem>
                                            <ListItemText
                                                primary={t('vehicles.table.vehicle')}
                                                secondary={selectedTicket.vehicle_name}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={t('hardcoded.pages_alarmticketpage.posicion_detail')}
                                                secondary={t('hardcoded.pages_alarmticketdetailpage.posicion_position_eje_axle', { position: selectedTicket.position, axle: selectedTicket.axle })}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={t('hardcoded.components_alarms_alarmlist.metrica')}
                                                secondary={normalizeMetricLabel(selectedTicket.metric_type, selectedTicket)}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={t('hardcoded.pages_alarmticketpage.estado_detail')}
                                                secondary={
                                                    <Chip
                                                        label={TICKET_STATUS_LABELS[selectedTicket.status]}
                                                        color={TICKET_STATUS_COLORS[selectedTicket.status]}
                                                        size="small"
                                                    />
                                                }
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.valores_y_umbrales')}</Typography>
                                    <List>
                                        {isNoReport(selectedTicket) ? (
                                            <>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={t('hardcoded.components_schematics_vehicleticketsdrawer.tiempo_sin_reportar')}
                                                        secondary={formatHumanElapsed(selectedTicket.current_value, selectedTicket.unit)}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={t('hardcoded.components_schematics_vehicleticketsdrawer.ultimo_reporte')}
                                                        secondary={
                                                            selectedTicket.evaluation_metadata?.last_seen_at
                                                                ? new Date(selectedTicket.evaluation_metadata.last_seen_at).toLocaleString('es-MX')
                                                                : '—'
                                                        }
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={t('hardcoded.pages_alarmticketpage.umbral_configurado')}
                                                        secondary={
                                                            selectedTicket.evaluation_metadata?.timeout_seconds
                                                                ? formatHumanElapsed(selectedTicket.evaluation_metadata.timeout_seconds, 'elapsed_seconds')
                                                                : '12h'
                                                        }
                                                    />
                                                </ListItem>
                                            </>
                                        ) : (
                                            <>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Valor Original"
                                                        secondary={`${selectedTicket.original_value} ${normalizeUnitLabel(selectedTicket.unit)}`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary="Valor Actual"
                                                        secondary={`${selectedTicket.current_value?.toFixed(1)} ${normalizeUnitLabel(selectedTicket.unit)}`}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={t('hardcoded.components_alarms_alarmform.umbral_minimo')}
                                                        secondary={selectedTicket.threshold_min ? `${selectedTicket.threshold_min} ${normalizeUnitLabel(selectedTicket.unit)}` : t('hardcoded.pages_alarmticketpage.no_configurado')}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={t('hardcoded.components_alarms_alarmform.umbral_maximo')}
                                                        secondary={selectedTicket.threshold_max ? `${selectedTicket.threshold_max} ${normalizeUnitLabel(selectedTicket.unit)}` : t('hardcoded.pages_alarmticketpage.no_configurado')}
                                                    />
                                                </ListItem>
                                            </>
                                        )}
                                    </List>
                                </Grid>

                                {/* Value History Chart */}
                                {selectedTicket.value_history && selectedTicket.value_history.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('hardcoded.pages_alarmticketpage.historial_completo_de_valores')}</Typography>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={createChartData(selectedTicket.value_history)}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="time" />
                                                <YAxis />
                                                <RechartsTooltip />
                                                <Line
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke={theme.palette.primary.main}
                                                    strokeWidth={2}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>
                        {t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Resolve Ticket Dialog */}
            <Dialog
                open={resolveDialogOpen}
                onClose={() => setResolveDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    {i18n.t('hardcoded.pages_alarmticketpage.resolver_ticket_manualmente')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t('hardcoded.pages_alarmticketpage.esta_accion_marcara_el_ticket')}</Alert>

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={t('hardcoded.pages_alarmticketdetailpage.notas_de_resolucion')}
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            placeholder={t('hardcoded.pages_alarmticketpage.describa_como_se_resolvio_el')}
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialogOpen(false)}>
                        {t('common.cancel')}</Button>
                    <Button
                        onClick={handleResolveTicket}
                        variant="contained"
                        disabled={!resolutionNotes.trim()}
                    >
                        {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.resolver_ticket')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AlarmTicketPage;